import { CaratPosition } from '@/typings/models/CaratModel';
import type { ColumnsType } from 'antd/es/table';
import { useIntl } from 'umi';
import { CARAT_EMPTY, CaratFormatter } from './format';

/** Hinge side of a position (`0500` value 6). "K" is Carat's "no side". */
export const CARAT_SIDES: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  L: { id: 'pages.integrations.carat.side_left', defaultMessage: 'Left' },
  R: { id: 'pages.integrations.carat.side_right', defaultMessage: 'Right' },
  K: { id: 'common.none', defaultMessage: 'None' },
};

/**
 * Columns of a Carat position table. Money is right-aligned so a group's prices
 * line up under its subtotal, and the description is allowed to wrap: Carat
 * writes it as up to a dozen wrapped lines and truncating it hides exactly the
 * detail this view exists for.
 */
export const useCaratPositionColumns = (
  format: CaratFormatter,
): ColumnsType<CaratPosition> => {
  const intl = useIntl();
  const label = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  return [
    {
      title: '#',
      dataIndex: 'position',
      width: 72,
      render: (value: string | null) => format.text(value),
    },
    {
      title: label('pages.integrations.carat.article', 'Article'),
      dataIndex: 'article_number',
      width: 140,
      render: (value: string | null) => format.text(value),
    },
    {
      title: label('common.attribute.description', 'Description'),
      dataIndex: 'description',
      render: (value: string | null) => format.text(value),
    },
    {
      title: label('pages.integrations.carat.quantity', 'Qty'),
      dataIndex: 'quantity',
      width: 64,
      align: 'right',
      render: (value: number | null) => format.number(value),
    },
    {
      title: label('pages.integrations.carat.dimensions', 'Dimensions'),
      dataIndex: 'dimensions',
      width: 170,
      render: (value: CaratPosition['dimensions']) => format.dimensions(value),
    },
    {
      title: label('pages.integrations.carat.list_price', 'List price'),
      dataIndex: ['price', 'list_gross'],
      width: 120,
      align: 'right',
      render: (value: number | null) => format.money(value),
    },
    {
      title: label('pages.billings.discount', 'Discount'),
      dataIndex: ['price', 'discount'],
      width: 120,
      align: 'right',
      render: (value: number | null) => format.signedMoney(value),
    },
    {
      title: label('pages.integrations.carat.gross', 'Gross'),
      dataIndex: ['price', 'gross'],
      width: 130,
      align: 'right',
      render: (value: number | null) => format.money(value),
    },
  ];
};

/**
 * Columns of the group breakdown — the subtotals a studio reads a Carat
 * quotation by.
 */
export const useCaratGroupColumns = (
  format: CaratFormatter,
): ColumnsType<any> => {
  const intl = useIntl();
  const label = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  return [
    {
      title: label('pages.integrations.carat.trade_group', 'Trade group'),
      dataIndex: 'label',
      render: (value: string | null, group: any) =>
        value || group.number || CARAT_EMPTY,
    },
    {
      title: label('pages.integrations.carat.positions', 'Positions'),
      dataIndex: 'positions',
      width: 110,
      align: 'right',
    },
    {
      title: label('pages.integrations.carat.net', 'Net'),
      dataIndex: 'total_net',
      width: 140,
      align: 'right',
      render: (value: number | null) => format.money(value),
    },
    {
      title: label('pages.integrations.carat.gross', 'Gross'),
      dataIndex: 'total_gross',
      width: 140,
      align: 'right',
      render: (value: number | null) => format.money(value),
    },
  ];
};

/** Columns of the configuration and per-position decor tables (`0410`/`0420`). */
export const useCaratAttributeColumns = (): ColumnsType<any> => {
  const intl = useIntl();
  const label = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  return [
    {
      title: label('entity.option.singular', 'Option'),
      dataIndex: 'label',
      width: '45%',
      render: (value: string | null, attribute: any) =>
        value || attribute.code || CARAT_EMPTY,
    },
    {
      title: label('pages.integrations.carat.selection', 'Selection'),
      dataIndex: 'value',
      render: (value: string | null) => value || CARAT_EMPTY,
    },
  ];
};
